import { Plus, Minus, Trash2, Printer, Wallet, Send, X, Receipt, ChefHat, CupSoda, UserPlus } from "lucide-react";
import { formatCurrency } from "../../lib/utils";

const inputCls =
  "w-full h-8 rounded-md bg-[#262626] border border-white/10 px-2 text-xs text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-emerald-500";

const Field = ({ label, required, children }) => (
  <label className="block min-w-0">
    <span className="text-[10px] text-zinc-400 font-medium">
      {label}
      {required && <span className="text-red-400">*</span>}
    </span>
    <div className="mt-0.5">{children}</div>
  </label>
);

const tableStatusLabel = (t, unpaidIds) => {
  if (unpaidIds.has(t.table_id)) return "Não paga";
  if (t.status === "cleaning") return "Paga";
  if (t.status === "reserved") return "Reservada";
  return "Livre";
};

const CustomerForm = ({ form, setField, tables, waiters, unpaidTableIds, onTableChosen }) => (
  <div className="p-3 border-b border-white/5 space-y-2" data-testid="pos-customer-form">
    <div className="grid grid-cols-2 gap-2">
      <Field label="Nome do Cliente" required>
        <div className="flex gap-1">
          <input
            className={inputCls}
            placeholder="Consumidor Final"
            value={form.customerName}
            onChange={(e) => setField("customerName", e.target.value)}
            data-testid="pos-customer-name"
          />
          <button
            type="button"
            onClick={() => setField("showNif", !form.showNif)}
            className={`w-8 h-8 shrink-0 rounded-md flex items-center justify-center text-white ${form.showNif ? "bg-emerald-500" : "bg-emerald-600 hover:bg-emerald-500"}`}
            title="Adicionar NIF do cliente"
            data-testid="pos-customer-nif-toggle"
          >
            {form.showNif ? <UserPlus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          </button>
        </div>
      </Field>
      <Field label="Tipo de Cliente" required>
        <select className={inputCls} value={form.orderType} onChange={(e) => setField("orderType", e.target.value)} data-testid="pos-customer-type">
          <option value="dine_in">Cliente de Mesa</option>
          <option value="takeaway">Balcão / Take-away</option>
          <option value="delivery">Entrega</option>
        </select>
      </Field>
    </div>
    {form.showNif && (
      <Field label="NIF do Cliente (para a fatura)">
        <input className={inputCls} placeholder="999999999" value={form.customerNif} onChange={(e) => setField("customerNif", e.target.value)} data-testid="pos-customer-nif" />
      </Field>
    )}
    <Field label="Empregado" required>
      <select className={inputCls} value={form.waiterId} onChange={(e) => setField("waiterId", e.target.value)} data-testid="pos-waiter-select">
        {waiters.map((w) => (
          <option key={w.user_id} value={w.user_id}>
            {w.name}
          </option>
        ))}
      </select>
    </Field>
    <div className="grid grid-cols-2 gap-2">
      <Field label="Mesa" required={form.orderType === "dine_in"}>
        <select
          className={inputCls}
          value={form.tableId}
          onChange={(e) => onTableChosen(e.target.value)}
          disabled={form.orderType !== "dine_in"}
          data-testid="pos-table-select"
        >
          <option value="">{form.orderType === "dine_in" ? "Selecionar mesa…" : "Sem mesa"}</option>
          {tables.map((t) => (
            <option key={t.table_id} value={t.table_id}>
              Mesa {t.number} · {tableStatusLabel(t, unpaidTableIds)}{unpaidTableIds.has(t.table_id) ? " (juntar ao pedido aberto)" : ""}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Tempo de Preparação">
        <select className={inputCls} value={form.cookingTime} onChange={(e) => setField("cookingTime", e.target.value)} data-testid="pos-cooking-time">
          <option value="">N/A</option>
          {["10 min", "15 min", "20 min", "30 min", "45 min"].map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </Field>
    </div>
  </div>
);

const ActiveOrderHeader = ({ order, tableLabel, onExit }) => (
  <div className="p-3 border-b border-white/5" data-testid="active-order-banner">
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-zinc-100">Pedido #{order.order_number}</p>
        <p className="text-[11px] text-zinc-400 truncate">
          {tableLabel} · {order.items.length} itens{order.waiter_name ? ` · ${order.waiter_name}` : ""}
          {order.customer_name ? ` · ${order.customer_name}` : ""}
        </p>
      </div>
      <span className="px-2 py-1 rounded bg-red-600 text-white text-[10px] font-black tracking-wide shrink-0" data-testid="active-order-unpaid-badge">
        {order.offline ? "OFFLINE · NÃO PAGA" : "NÃO PAGA"}
      </span>
      <button onClick={onExit} className="w-7 h-7 rounded flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10" data-testid="exit-active-order" aria-label="Sair do pedido">
        <X className="w-4 h-4" />
      </button>
    </div>
  </div>
);

const QtyControl = ({ id, qty, onQty }) => (
  <div className="flex items-center justify-center gap-1">
    <button onClick={() => onQty(id, -1)} className="w-5 h-5 rounded bg-emerald-600/80 hover:bg-emerald-500 text-white flex items-center justify-center" data-testid={`qty-minus-${id}`}>
      <Minus className="w-3 h-3" />
    </button>
    <span className="w-5 text-center font-bold text-zinc-100" data-testid={`qty-value-${id}`}>{qty}</span>
    <button onClick={() => onQty(id, 1)} className="w-5 h-5 rounded bg-emerald-600/80 hover:bg-emerald-500 text-white flex items-center justify-center" data-testid={`qty-plus-${id}`}>
      <Plus className="w-3 h-3" />
    </button>
  </div>
);

const ItemsTable = ({ existingItems, cart, onQty, onRemove, onNote, onRemoveExisting }) => (
  <div className="flex-1 min-h-0 min-h-[96px] overflow-y-auto px-3 pt-1" data-testid="pos-cart-table">
    <table className="w-full text-[11px]">
      <thead className="sticky top-0 bg-[#1b1b1b] z-10">
        <tr className="text-zinc-400 text-left border-b border-white/10">
          <th className="py-1.5 font-medium">Artigo</th>
          <th className="py-1.5 font-medium text-right">Preço</th>
          <th className="py-1.5 font-medium text-center">Qtd</th>
          <th className="py-1.5 font-medium text-right">Total</th>
          <th className="py-1.5 w-8" />
        </tr>
      </thead>
      <tbody>
        {existingItems.map((it, idx) => (
          <tr key={it.item_id || idx} className="border-b border-white/5 text-zinc-400" data-testid={`pos-existing-row-${it.item_id || idx}`}>
            <td className="py-1.5 pr-1">
              <p className="leading-tight">
                {it.product_name}
                {it.promotion_name && <span className="ml-1 text-[9px] font-black bg-red-600/80 text-white px-1 py-px rounded align-middle">PROMO</span>}
              </p>
              {it.notes && <p className="text-[10px] text-amber-400/70">{it.notes}</p>}
            </td>
            <td className="text-right font-mono whitespace-nowrap px-1">{formatCurrency(it.unit_price)}</td>
            <td className="text-center font-bold px-1">{it.quantity}</td>
            <td className="text-right font-mono whitespace-nowrap px-1">{formatCurrency(it.line_total ?? it.unit_price * it.quantity)}</td>
            <td className="text-right">
              {onRemoveExisting ? (
                <button
                  onClick={() => onRemoveExisting({ ...it, item_id: it.item_id || `idx-${idx}` })}
                  title="Remover item do pedido (repõe o stock)"
                  className="w-6 h-6 rounded bg-zinc-700 hover:bg-red-600 text-zinc-300 hover:text-white inline-flex items-center justify-center transition-colors"
                  data-testid={`remove-existing-item-${it.item_id || idx}`}
                >
                  <X className="w-3 h-3" />
                </button>
              ) : (
                <span className="text-[9px] uppercase tracking-wide text-zinc-500">enviado</span>
              )}
            </td>
          </tr>
        ))}
        {cart.map((item) => {
          const left = item.track_stock ? (item.current_stock ?? 0) - item.quantity : null;
          const lowRow = left !== null && left <= (item.min_stock ?? 0);
          return (
          <tr key={item.product_id} className={`border-b border-white/5 ${lowRow ? "bg-amber-500/10" : ""}`} data-testid={`pos-cart-row-${item.product_id}`}>
            <td className="py-1.5 pr-1">
              <p className="font-medium text-zinc-100 leading-tight">
                {item.name}
                {item.promotion_name && (
                  <span className="ml-1 text-[9px] font-black bg-red-600 text-white px-1 py-px rounded align-middle" data-testid={`pos-cart-promo-${item.product_id}`} title={item.promotion_name}>PROMO</span>
                )}
                {lowRow && (
                  <span className={`ml-1 text-[9px] font-bold px-1 py-px rounded align-middle ${left < 0 ? "bg-red-600 text-white" : "bg-amber-500 text-black"}`} data-testid={`pos-cart-low-stock-${item.product_id}`}>
                    {left < 0 ? "SEM STOCK" : `STOCK BAIXO · ficam ${left}`}
                  </span>
                )}
              </p>
              <input
                value={item.notes || ""}
                onChange={(e) => onNote(item.product_id, e.target.value)}
                placeholder="+ nota (ex: sem cebola)"
                className="mt-0.5 w-full bg-transparent text-[10px] text-amber-300 placeholder:text-zinc-600 focus:outline-none"
                data-testid={`pos-cart-note-${item.product_id}`}
              />
            </td>
            <td className="text-right font-mono text-zinc-300 whitespace-nowrap px-1">
              {item.original_price != null && item.original_price > item.price && (
                <span className="block text-[9px] text-zinc-500 line-through leading-none">{formatCurrency(item.original_price)}</span>
              )}
              {formatCurrency(item.price)}
            </td>
            <td className="px-1">
              <QtyControl id={item.product_id} qty={item.quantity} onQty={onQty} />
            </td>
            <td className="text-right font-mono font-semibold text-zinc-100 whitespace-nowrap px-1">{formatCurrency(item.price * item.quantity)}</td>
            <td className="text-right">
              <button onClick={() => onRemove(item.product_id)} className="w-6 h-6 rounded bg-red-600/90 hover:bg-red-500 text-white inline-flex items-center justify-center" data-testid={`remove-item-${item.product_id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </td>
          </tr>
          );
        })}
      </tbody>
    </table>
    {existingItems.length === 0 && cart.length === 0 && (
      <p className="text-center text-xs text-zinc-500 py-10" data-testid="pos-cart-empty">Toque nos artigos para adicionar ao pedido</p>
    )}
  </div>
);

const Totals = ({ subtotal, tax, grand, hint }) => (
  <div className="px-3 pt-2 space-y-2">
    {hint}
    <div className="grid grid-cols-2 gap-2">
      <div className="rounded-md bg-[#262626] px-2.5 py-1.5 flex justify-between text-[11px]">
        <span className="text-zinc-400">Subtotal s/ IVA</span>
        <span className="font-mono text-zinc-100" data-testid="pos-subtotal">{formatCurrency(subtotal)}</span>
      </div>
      <div className="rounded-md bg-[#262626] px-2.5 py-1.5 flex justify-between text-[11px]">
        <span className="text-zinc-400">IVA incluído</span>
        <span className="font-mono text-zinc-100" data-testid="pos-tax">{formatCurrency(tax)}</span>
      </div>
    </div>
    <div className="rounded-md bg-emerald-600 px-3 py-2 flex items-center justify-between shadow-lg shadow-emerald-900/30">
      <span className="text-xs font-bold text-white">Total Geral</span>
      <span className="font-mono text-lg font-black text-white" data-testid="pos-grand-total">{formatCurrency(grand)}</span>
    </div>
  </div>
);

const ActionBtn = ({ className = "", children, ...rest }) => (
  <button
    className={`h-11 rounded-md text-xs font-bold text-white flex items-center justify-center gap-1.5 transition-[background-color,opacity] disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
    {...rest}
  >
    {children}
  </button>
);

export const POSOrderPanel = ({
  activeOrder,
  tableLabel,
  form,
  setField,
  tables,
  waiters,
  unpaidTableIds,
  cart,
  onQty,
  onRemove,
  onNote,
  onRemoveExisting,
  totals,
  routing,
  busy,
  onClear,
  onPreBill,
  onPayNow,
  onPlaceOrder,
  onAddToOrder,
  onCloseTable,
  onCancelOrder,
  onExitOrder,
  onTableChosen,
}) => {
  const { foodQty, drinkQty, onlyDrinks, mixed } = routing;
  const hint = mixed ? (
    <div className="rounded-md bg-sky-500/10 border border-sky-500/30 px-2.5 py-1.5 text-[11px] text-sky-200 flex items-center gap-2" data-testid="mixed-order-info">
      <ChefHat className="w-3.5 h-3.5 shrink-0" />
      <span>
        <strong>{foodQty} comida</strong> → Cozinha · <strong>{drinkQty} bebida(s)</strong> → Balcão
      </span>
    </div>
  ) : onlyDrinks ? (
    <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 px-2.5 py-1.5 text-[11px] text-emerald-200 flex items-center gap-2" data-testid="drinks-only-info">
      <CupSoda className="w-3.5 h-3.5 shrink-0" />
      <span>Só bebidas — pode <strong>pagar e faturar já</strong>, sem passar pela cozinha</span>
    </div>
  ) : null;

  const placeLabel = cart.length === 0 || onlyDrinks || mixed ? "Enviar Pedido" : "Enviar p/ Cozinha";

  return (
    <aside className="w-[400px] shrink-0 bg-[#1b1b1b] border-l border-white/5 flex flex-col min-h-0" data-testid="pos-order-panel">
      {activeOrder ? (
        <ActiveOrderHeader order={activeOrder} tableLabel={tableLabel} onExit={onExitOrder} />
      ) : (
        <CustomerForm form={form} setField={setField} tables={tables} waiters={waiters} unpaidTableIds={unpaidTableIds} onTableChosen={onTableChosen} />
      )}
      <ItemsTable existingItems={activeOrder?.items || []} cart={cart} onQty={onQty} onRemove={onRemove} onNote={onNote} onRemoveExisting={activeOrder ? onRemoveExisting : null} />
      <Totals subtotal={totals.subtotal} tax={totals.tax} grand={totals.grand} hint={hint} />
      <div className="p-3 grid grid-cols-[40px_40px_1fr_1fr] gap-2">
        <ActionBtn className="bg-sky-600 hover:bg-sky-500" onClick={onPreBill} title="Consulta de mesa (documento de conferência assinado)" data-testid="pos-prebill-btn" disabled={!activeOrder}>
          <Printer className="w-4 h-4" />
        </ActionBtn>
        <ActionBtn className="bg-red-600 hover:bg-red-500" onClick={onClear} title="Limpar" data-testid="pos-clear-btn" disabled={cart.length === 0}>
          <Trash2 className="w-4 h-4" />
        </ActionBtn>
        {activeOrder ? (
          <>
            <ActionBtn className="bg-red-600 hover:bg-red-500" onClick={onCloseTable} disabled={busy} data-testid="pos-close-table-btn">
              <Receipt className="w-4 h-4" /> Fechar & Faturar
            </ActionBtn>
            <ActionBtn className="bg-emerald-600 hover:bg-emerald-500" onClick={onAddToOrder} disabled={cart.length === 0 || busy} data-testid="pos-add-to-order-btn">
              <Plus className="w-4 h-4" /> Adicionar Itens
            </ActionBtn>
          </>
        ) : (
          <>
            <ActionBtn className="bg-red-600 hover:bg-red-500" onClick={onPayNow} disabled={cart.length === 0 || busy} data-testid="pos-pay-now-btn">
              <Wallet className="w-4 h-4" /> Pagar Agora
            </ActionBtn>
            <ActionBtn className="bg-emerald-600 hover:bg-emerald-500" onClick={onPlaceOrder} disabled={cart.length === 0 || busy} data-testid="pos-place-order-btn">
              <Send className="w-4 h-4" /> {placeLabel}
            </ActionBtn>
          </>
        )}
      </div>
      {activeOrder && (
        <button onClick={onCancelOrder} className="mx-3 mb-3 -mt-1 text-[11px] text-red-400 hover:text-red-300 text-left" data-testid="pos-cancel-order-btn">
          Cancelar pedido #{activeOrder.order_number}
        </button>
      )}
    </aside>
  );
};
